import React, { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { getCombatManager } from "../../engine/combat/combatManagerRef.js";
import { factionColor } from "../../engine/types/factionColor.js";
import type { EnemyFaction } from "../../engine/types/entity.js";
import { getRendererContext } from "../rendererContext.js";

/**
 * Floating cooldown meter anchored above each engaged enemy's sprite.
 *
 * Re-reads `CombatManager.listEngagedEnemies()` on every RAF tick so the
 * set of visible bars follows the live combat state (new aggros appear,
 * dead enemies vanish). World-space anchors are projected to canvas pixels
 * via the shared renderer context.
 *
 * Rendered into a full-screen, pointer-events:none layer so clicks still
 * fall through to the Three.js canvas.
 */

const ANCHOR_OFFSET_Y = 0.75;   // world units above the sprite center
const BAR_WIDTH = 38;           // px
const BAR_HEIGHT = 4;           // px

interface BarEntry {
  readonly instanceId: string;
  readonly left: number;
  readonly top: number;
  readonly fillPct: number;
  readonly faction: EnemyFaction;
}

export function EnemyMeter() {
  const [bars, setBars] = useState<readonly BarEntry[]>([]);
  const projBufRef = useRef(new THREE.Vector3());

  useEffect(() => {
    let raf = 0;

    const step = () => {
      const cm = getCombatManager();
      const ctx = getRendererContext();
      if (!cm || !ctx) {
        setBars((prev) => (prev.length === 0 ? prev : []));
        raf = requestAnimationFrame(step);
        return;
      }

      const rect = ctx.canvas.getBoundingClientRect();
      const next: BarEntry[] = [];
      for (const e of cm.listEngagedEnemies()) {
        if (!e.worldPos) continue;
        const v = projBufRef.current;
        v.set(e.worldPos.x, e.worldPos.y + ANCHOR_OFFSET_Y, e.worldPos.z);
        v.project(ctx.camera);

        // Skip enemies behind the camera — project() flips z into [-1,1]
        // but NDC.z > 1 means the anchor is behind the near plane.
        if (v.z > 1 || v.z < -1) continue;

        const left = rect.left + ((v.x + 1) / 2) * rect.width - BAR_WIDTH / 2;
        const top = rect.top + ((1 - v.y) / 2) * rect.height - BAR_HEIGHT / 2;
        const fillPct =
          e.duration > 0
            ? Math.max(0, Math.min(100, (1 - e.remaining / e.duration) * 100))
            : 100;
        next.push({ instanceId: e.instanceId, left, top, fillPct, faction: e.faction });
      }

      setBars((prev) => {
        if (prev.length !== next.length) return next;
        for (let i = 0; i < prev.length; i++) {
          const a = prev[i]!;
          const b = next[i]!;
          if (
            a.instanceId !== b.instanceId ||
            a.faction !== b.faction ||
            Math.abs(a.left - b.left) > 0.5 ||
            Math.abs(a.top - b.top) > 0.5 ||
            Math.abs(a.fillPct - b.fillPct) > 0.5
          ) {
            return next;
          }
        }
        return prev;
      });

      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, []);

  if (bars.length === 0) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        pointerEvents: "none",
        zIndex: 120,
      }}
    >
      {bars.map((b) => {
        const color = factionColor(b.faction);
        return (
          <div
            key={b.instanceId}
            data-testid={`enemy-meter-${b.instanceId}`}
            data-faction={b.faction}
            style={{
              position: "absolute",
              left: b.left,
              top: b.top,
              width: BAR_WIDTH,
              height: BAR_HEIGHT,
              background: "rgba(0,0,0,0.7)",
              border: `1px solid ${color}`,
              borderRadius: 1,
              overflow: "hidden",
            }}
          >
            <div
              style={{
                height: "100%",
                width: `${b.fillPct}%`,
                background: color,
              }}
            />
          </div>
        );
      })}
    </div>
  );
}
